import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

type ContainsFn = (target: Node) => boolean;

interface PortalMenuRegistry {
  registerChild: (fn: ContainsFn) => () => void;
}

const PortalMenuContext = createContext<PortalMenuRegistry | null>(null);

interface PopperMenuProps {
  toggle: React.ReactNode;
  toggleClassName?: string;
  toggleTitle?: string;
  toggleDisabled?: boolean;
  /** Render prop — receives a `close` callback so children can close the menu */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  minWidth?: number;
  align?: 'left' | 'right';
  menuStyle?: React.CSSProperties;
  /** Additional className for the portal div (default: "dropdown-menu show") */
  menuClassName?: string;
  /** Stretch the portal menu to full viewport width, anchored below the toggle */
  fullWidth?: boolean;
  /** Size the menu panel to match the toggle button's width instead of `minWidth` — for a full-width toggle (e.g. a mobile filter selector). */
  matchReferenceWidth?: boolean;
  /** Render in the higher stacking tier (z-index 1150), above the navbar (1020), the deep-dive side panel (1044-1049), bootstrap modals (1050-1055), and the Settings drawer (1100-1101) — for navbar-anchored menus (profile, notifications) and anything nested inside one of those elevated containers, which would otherwise render behind them. Default false: the lower tier (z-index 1000), below all of them. */
  elevated?: boolean;
  onOpen?: () => void;
}

/**
 * Drop-in, Popper.js-based replacement for the original `PortalMenu` (same
 * props, same render-prop `children`) — built for `MenuList`, then extracted
 * here so other floating triggers can share it instead of each hand-rolling
 * position math. Popper correctly handles scroll containers and transformed
 * ancestors (e.g. react-grid-layout's `.react-grid-item`) with no manual
 * listeners, and the menu is portaled to `document.body` so it also escapes
 * sibling-stacking/overflow-clipping.
 */
export default function PopperMenu({
  toggle,
  toggleClassName = 'btn btn-link btn-sm p-1 py-0',
  toggleTitle,
  toggleDisabled,
  children,
  minWidth = 160,
  align = 'left',
  menuStyle,
  menuClassName = 'dropdown-menu show',
  fullWidth,
  matchReferenceWidth,
  elevated = false,
  onOpen,
}: PopperMenuProps) {
  const [open, setOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [fullWidthTop, setFullWidthTop] = useState(0);

  const parentRegistry = useContext(PortalMenuContext);
  const childContainsFns = useRef<Set<ContainsFn>>(new Set());

  const containsElementRef = useRef<ContainsFn>(() => false);
  containsElementRef.current = target => {
    if (popperElement?.contains(target)) return true;
    for (const fn of childContainsFns.current) {
      if (fn(target)) return true;
    }
    return false;
  };

  const registry = useRef<PortalMenuRegistry>({
    registerChild: fn => {
      childContainsFns.current.add(fn);
      return () => childContainsFns.current.delete(fn);
    },
  }).current;

  useEffect(() => {
    if (!open || !parentRegistry) return;
    return parentRegistry.registerChild(target => containsElementRef.current(target));
  }, [open, parentRegistry]);

  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, 4] } },
      { name: 'preventOverflow', options: { rootBoundary: 'viewport', altAxis: true, padding: 8 } },
      ...(matchReferenceWidth
        ? [
            {
              name: 'sameWidth',
              enabled: true,
              phase: 'beforeWrite',
              requires: ['computeStyles'],
              fn: ({ state }: any) => {
                state.styles.popper.width = `${state.rects.reference.width}px`;
              },
            } as any,
          ]
        : []),
    ],
    [matchReferenceWidth],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: 'fixed',
    placement: align === 'right' ? 'bottom-end' : 'bottom-start',
    modifiers,
  });

  const close = () => setOpen(false);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) {
      if (fullWidth && referenceElement) {
        setFullWidthTop(referenceElement.getBoundingClientRect().bottom + 4);
      }
      onOpen?.();
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containsElementRef.current(e.target as Node)) return;
      if (referenceElement?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open, referenceElement]);

  // Below the navbar (1020) and the deep-dive rail (1044-1049) by default;
  // elevated menus render above those, bootstrap modals (1050-1055), and the
  // Settings drawer (1100-1101) instead — headroom is left above 1101 for
  // one more future overlay tier.
  const zIndex = elevated ? 1150 : 1000;

  const style: React.CSSProperties = fullWidth
    ? {
        position: 'fixed',
        top: fullWidthTop,
        left: 0,
        width: '100vw',
        zIndex,
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        ...menuStyle,
      }
    : {
        ...styles.popper,
        zIndex,
        ...(matchReferenceWidth ? {} : { minWidth }),
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        ...menuStyle,
      };

  return (
    <>
      <button
        ref={setReferenceElement}
        className={toggleClassName}
        title={toggleTitle}
        disabled={toggleDisabled}
        onClick={handleToggle}
      >
        {toggle}
      </button>

      {open &&
        createPortal(
          <div
            ref={setPopperElement}
            className={menuClassName}
            style={style}
            {...(fullWidth ? {} : attributes.popper)}
            onClick={e => e.stopPropagation()}
          >
            <PortalMenuContext.Provider value={registry}>
              {typeof children === 'function' ? children(close) : children}
            </PortalMenuContext.Provider>
          </div>,
          document.body,
        )}
    </>
  );
}
